// Address search through the gateway's `/geocode` (atlas-search spec). No key here: the gateway
// holds it. Debounced, 3-character minimum, and results outside the metro are dropped again.

import { GATEWAY, METRO_BBOX } from "./config";
import { h } from "./dom";

export interface Place {
  label: string;
  lng: number;
  lat: number;
}

export const DEBOUNCE_MS = 300;
export const MIN_CHARS = 3;
export const UNAVAILABLE = "Search unavailable";
export const TRY_LATER = "Search unavailable, try later";

export function inMetro({ lng, lat }: { lng: number; lat: number }): boolean {
  const [w, s, e, n] = METRO_BBOX;
  return lng >= w && lng <= e && lat >= s && lat <= n;
}

export interface SearchDeps {
  fetcher?: typeof fetch;
  onSelect(place: Place): void;
  debounceMs?: number;
}

export function attachSearch(form: HTMLFormElement, deps: SearchDeps): void {
  const input = form.querySelector("input")!;
  const list = form.querySelector<HTMLUListElement>("[data-results]")!;
  const status = form.querySelector<HTMLElement>("[data-status]")!;
  const fetcher = deps.fetcher ?? fetch.bind(globalThis);
  let timer: ReturnType<typeof setTimeout> | undefined;
  let controller: AbortController | undefined;
  let results: Place[] = [];

  const show = (places: Place[], message = "") => {
    results = places;
    status.textContent = message;
    list.replaceChildren(
      ...places.map((p, i) => {
        const button = h("button", { type: "button", role: "option", "data-index": String(i) }, p.label);
        button.addEventListener("click", () => select(p));
        return h("li", {}, button);
      }),
    );
    input.setAttribute("aria-expanded", String(places.length > 0));
  };

  const select = (p: Place) => {
    input.value = p.label;
    show([]);
    deps.onSelect(p);
  };

  /** Shows the results; resolves to them, or to null when superseded or failed. */
  const run = async (q: string): Promise<Place[] | null> => {
    controller?.abort();
    controller = new AbortController();
    try {
      const res = await fetcher(`${GATEWAY}/geocode?q=${encodeURIComponent(q)}`, { signal: controller.signal });
      if (!res.ok) {
        show([], res.status === 429 ? TRY_LATER : UNAVAILABLE);
        return null;
      }
      const body = (await res.json()) as { results?: Place[] };
      const places = (body.results ?? []).filter((p) => Number.isFinite(p.lng) && Number.isFinite(p.lat) && inMetro(p));
      show(places, places.length ? "" : "No Austin-area match. Add the city, e.g. \", Austin\"");
      return places;
    } catch (e) {
      if ((e as Error).name !== "AbortError") show([], UNAVAILABLE);
      return null;
    }
  };

  input.addEventListener("input", () => {
    clearTimeout(timer);
    const q = input.value.trim();
    if (q.length < MIN_CHARS) {
      controller?.abort();
      return show([]);
    }
    timer = setTimeout(() => void run(q), deps.debounceMs ?? DEBOUNCE_MS);
  });

  form.addEventListener("submit", async (ev) => {
    ev.preventDefault();
    if (results[0]) return select(results[0]);
    // Enter before the debounce fired (or its answer arrived): search now and take the first match.
    const q = input.value.trim();
    if (q.length < MIN_CHARS) return;
    clearTimeout(timer);
    const places = await run(q);
    if (places?.[0] && input.value.trim() === q) select(places[0]);
  });
}
